<script setup lang="ts">
  /**
   * Extract field — 03-components/13a-detail-card (`.detail-row__field`) · OSCloud DS 1.1.116
   *
   * The third kind of value, after the fact you read and the fact you edit: the fact you
   * EXTRACT — a pairing code, a backup code, a server ID. The frame surrounds the value alone, so
   * it shows the reader exactly what copy will copy; the label stays outside it, in the
   * surrounding detail row. Body face with wide tracking and tabular numerals — not monospace.
   *
   * Copy writes the string to the clipboard and confirms with a success toast — the view that
   * renders the field mounts BfToastViewport.
   */
  import { computed } from 'vue'

  import { useNotificationStore } from '@/store/notification'
  import BfIconCopy from '@/components/ui/icons/BfIconCopy.vue'

  const props = withDefaults(
    defineProps<{
      value: string
      /** What the value is, e.g. "Backup code". Names the copy action and its confirmation. */
      label: string
      /** Renders the copy action. Off for a value that is only highlighted, not taken away. */
      copyable?: boolean
      /** Holds the copy action disabled — while the value is still loading, say. */
      disabled?: boolean
      /** Headline of the copy toast, e.g. the page's name; the confirmation becomes its detail. */
      toastTitle?: string
    }>(),
    { copyable: true, disabled: false },
  )

  const emit = defineEmits<{ copy: [value: string] }>()

  const { addNotification } = useNotificationStore()

  const copyLabel = computed<string>(() => `Copy ${props.label.toLowerCase()}`)

  async function onCopy() {
    await navigator.clipboard.writeText(props.value)
    const confirmation = `${props.label} copied`
    addNotification({
      type: 'success',
      content: props.toastTitle ?? confirmation,
      description: props.toastTitle ? confirmation : undefined,
      withTimer: true,
      asToast: true,
    })
    emit('copy', props.value)
  }
</script>

<template>
  <span class="detail-row__field">
    <code class="detail-row__field-value">{{ value }}</code>
    <button
      v-if="copyable"
      type="button"
      class="detail-row__field-action"
      :aria-label="copyLabel"
      :disabled="disabled"
      @click="onCopy"
    >
      <BfIconCopy />
    </button>
  </span>
</template>

<style scoped lang="scss">
  .detail-row__field {
    /* The two hooks the DS exposes; light surfaces take a dark ink, never a tone. */
    --detail-field-surface: var(--bf-blue-light);
    --detail-field-value-color: var(--bf-fg-2);
    display: flex;
    align-items: center;
    gap: var(--bf-sp-1);
    width: 100%;
    min-width: 0;
    padding: 5px 6px 5px 10px;
    background: var(--detail-field-surface);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-md);
  }

  /* overflow-wrap: anywhere — a long string wraps inside the frame instead of widening the
     column, and the action keeps the trailing edge. */
  .detail-row__field-value {
    flex: 1 1 auto;
    min-width: 0;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-sm);
    letter-spacing: var(--bf-tr-wide);
    font-variant-numeric: tabular-nums;
    color: var(--detail-field-value-color);
    overflow-wrap: anywhere;
  }

  /* A fill, not an edge: this is the one control on a single output box, so nothing else
     reacts on hover (DS 1.1.124). */
  .detail-row__field-action {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: var(--bf-r-sm);
    background: none;
    color: var(--bf-fg-2);
    cursor: pointer;
    transition:
      color var(--bf-dur-base) var(--bf-ease),
      background var(--bf-dur-base) var(--bf-ease);
  }

  .detail-row__field-action:hover:not(:disabled) {
    color: var(--bf-orange-dark);
    background: var(--bf-white);
  }

  .detail-row__field-action:focus-visible {
    outline: 2px solid hsl(var(--dk-ring));
    outline-offset: 2px;
  }

  .detail-row__field-action:disabled {
    color: var(--bf-fg-3);
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    .detail-row__field-action {
      transition: none;
    }
  }
</style>
